import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Search, TrendingUp, Clock, Plus } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { studentRolesDiscoveryService } from '../../services/studentRolesDiscoveryService';
import { useRealtimeSync } from '../../hooks/useRealtimeSync';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

export default function StudentRolesDiscovery() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Content state
  const [continueLearning, setContinueLearning] = useState([]);
  const [popularRoles, setPopularRoles] = useState([]);
  const [recentlyUpdated, setRecentlyUpdated] = useState([]);
  const [recentlyAdded, setRecentlyAdded] = useState([]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // ✅ Real-time sync for roles - students see updates instantly
  useRealtimeSync('roles', {
    onInsert: (newRole) => {
      console.log('New role available:', newRole);
      // Show notification and refetch
      fetchRoles();
    },
    onUpdate: (updatedRole) => {
      console.log('Role content updated:', updatedRole);
      // Update the role in the current view
      setRoles(prevRoles => 
        prevRoles?.map(role => 
          role?.id === updatedRole?.id ? { ...role, ...updatedRole } : role
        )
      );
    },
    onDelete: (deletedRole) => {
      console.log('Role removed:', deletedRole);
      // Remove from student's view
      setRoles(prevRoles => 
        prevRoles?.filter(role => role?.id !== deletedRole?.id)
      );
    }
  });

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError('');

      const [continueData, popularData, updatedData, addedData] = await Promise.all([
        studentRolesDiscoveryService?.getContinueLearningRoles(user?.id, 12),
        studentRolesDiscoveryService?.getPopularRoles(12),
        studentRolesDiscoveryService?.getRecentlyUpdatedRoles(12),
        studentRolesDiscoveryService?.getRecentlyAddedRoles(12)
      ]);

      setContinueLearning(continueData);
      setPopularRoles(popularData);
      setRecentlyUpdated(updatedData);
      setRecentlyAdded(addedData);
    } catch (err) {
      console.error('Error loading roles:', err);
      setError(err?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchRoles();
    }
  }, [user]);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery?.trim()) {
      setSearchResults(null);
      return;
    }

    try {
      setSearchLoading(true);
      const results = await studentRolesDiscoveryService?.searchRoles(searchQuery);
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching:', err);
      setError(err?.message || 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const navigateToRole = (roleId) => {
    navigate(`/role-detail-page?id=${roleId}`);
  };

  const RoleCard = ({ role }) => (
    <div
      onClick={() => navigateToRole(role?.id)}
      className="flex-shrink-0 w-48 cursor-pointer hover:scale-105 transition-transform"
    >
      <img
        src={role?.musicalPoster || '/assets/images/no_image.png'}
        alt={role?.musicalTitle}
        className="w-full aspect-[2/3] object-cover rounded-lg mb-2"
      />
      <p className="text-white text-sm font-medium truncate">{role?.characterName}</p>
      <p className="text-purple-300 text-xs truncate">{role?.musicalTitle}</p>
      <div className="flex gap-2 mt-1">
        <span className="inline-block px-2 py-1 bg-purple-500/30 text-purple-200 rounded text-xs">
          {role?.voiceType}
        </span>
        {role?.songCount > 0 && (
          <span className="inline-block px-2 py-1 bg-blue-500/30 text-blue-200 rounded text-xs">
            {role?.songCount} songs
          </span>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Roles - RoleCall Student Discovery</title>
        <meta name="description" content="Discover and explore musical theater character roles with comprehensive learning pathways" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-500/10 border border-red-500 rounded-xl p-4">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* Search Bar */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Search roles..."
                  className="w-full bg-white/5 border border-white/20 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-purple-400"
                />
              </div>
              <button
                type="submit"
                disabled={searchLoading}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </button>
            </form>

            {/* Search Results */}
            {searchResults && (
              <div className="mt-6">
                <h3 className="text-white font-semibold mb-3">Search Results ({searchResults?.length})</h3>
                {searchResults?.length > 0 ? (
                  <div className="space-y-2">
                    {searchResults?.map((role) => (
                      <div
                        key={role?.id}
                        onClick={() => navigateToRole(role?.id)}
                        className="flex gap-3 p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors"
                      >
                        <img
                          src={role?.musicalPoster || '/assets/images/no_image.png'}
                          alt={role?.musicalTitle}
                          className="w-16 h-24 object-cover rounded flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{role?.characterName}</p>
                          <p className="text-purple-300 text-sm truncate">{role?.musicalTitle}</p>
                          <div className="flex gap-2 mt-1">
                            <span className="inline-block px-2 py-1 bg-purple-500/30 text-purple-200 rounded text-xs">
                              {role?.voiceType}
                            </span>
                            {role?.songCount > 0 && (
                              <span className="inline-block px-2 py-1 bg-blue-500/30 text-blue-200 rounded text-xs">
                                {role?.songCount} songs
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/70">No roles found matching "{searchQuery}"</p>
                )}
              </div>
            )}
          </div>

          {/* Continue Learning */}
          {continueLearning?.length > 0 && (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Clock className="w-6 h-6 text-blue-400" />
                Continue Learning
              </h2>
              <div className="overflow-x-auto">
                <div className="flex gap-4 pb-4">
                  {continueLearning?.map((role) => (
                    <RoleCard key={role?.id} role={role} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Popular Roles */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-yellow-400" />
              Popular Roles
            </h2>
            <div className="overflow-x-auto">
              <div className="flex gap-4 pb-4">
                {popularRoles?.map((role) => (
                  <RoleCard key={role?.id} role={role} />
                ))}
              </div>
            </div>
          </div>

          {/* Recently Updated Roles */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Clock className="w-6 h-6 text-green-400" />
              Recently Updated Roles
            </h2>
            <div className="overflow-x-auto">
              <div className="flex gap-4 pb-4">
                {recentlyUpdated?.map((role) => (
                  <RoleCard key={role?.id} role={role} />
                ))}
              </div>
            </div>
          </div>

          {/* Recently Added Roles */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Plus className="w-6 h-6 text-purple-400" />
              Recently Added Roles
            </h2>
            <div className="overflow-x-auto">
              <div className="flex gap-4 pb-4">
                {recentlyAdded?.map((role) => (
                  <RoleCard key={role?.id} role={role} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}